import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { updateAppointment } from '../../../Services/APIs/patient';
import { getAvailableTimeSlots, getLocalDateString } from '../../../Utils/timeSlots';

const formatDate = (dateStr) => {
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return dateStr;
    }
};

const RescheduleModal = ({ booking, email, onClose, onSuccess }) => {
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [error, setError] = useState('');

    const minDate = useMemo(() => getLocalDateString(), []);

    const clinic = { working_hours: booking.working_hours, sunday_hours: booking.sunday_hours };
    const availableSlots = selectedDate ? getAvailableTimeSlots(clinic, selectedDate) : [];

    const canSubmit = selectedDate && selectedTime && !isLoading;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsLoading(true);
        setError('');
        try {
            await updateAppointment({
                _id: booking.id,
                appointment_date: new Date(selectedDate).toISOString(),
                appointment_time: selectedTime,
                is_rescheduled: true,
                email,
                name: booking.memberName,
                phone: booking.memberPhone,
                uid: booking.memberId,
            });
            const result = { date: formatDate(selectedDate), time: selectedTime };
            setSuccessData(result);
            setIsSuccess(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to reschedule. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden">

                {isSuccess ? (
                    <div className="p-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-gray-800 font-bold text-lg mb-1">Appointment Rescheduled</h3>
                        <p className="text-gray-400 text-sm mb-4">Your appointment has been successfully rescheduled.</p>
                        <div className="bg-gray-50 rounded-xl px-5 py-3 w-full mb-6 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-400">New Date</span>
                                <span className="text-gray-800 font-semibold">{successData?.date}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">New Time</span>
                                <span className="text-gray-800 font-semibold">{successData?.time}</span>
                            </div>
                        </div>
                        <button
                            onClick={() => onSuccess(successData)}
                            className="w-full py-3 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="bg-indigo-700 px-5 py-4 flex items-center justify-between flex-shrink-0">
                            <div>
                                <h3 className="text-white font-bold text-base">Reschedule Appointment</h3>
                                <p className="text-indigo-200 text-xs mt-0.5 truncate">{booking.clinicName}</p>
                            </div>
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all flex-shrink-0 ml-3"
                            >
                                <X className="w-4 h-4 text-white" />
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto p-5">
                            <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">Select Date</p>
                            <input
                                type="date"
                                min={minDate}
                                value={selectedDate}
                                onChange={(e) => { setSelectedDate(e.target.value); setSelectedTime(''); }}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 mb-5 cursor-pointer"
                            />

                            <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">Select Time</p>
                            <div className="relative">
                                <select
                                    value={selectedTime}
                                    onChange={(e) => setSelectedTime(e.target.value)}
                                    disabled={availableSlots.length === 0}
                                    className="w-full appearance-none border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <option value="" disabled>
                                        {selectedDate && availableSlots.length === 0 ? 'No slots available' : 'Choose a time'}
                                    </option>
                                    {availableSlots.map((slot) => (
                                        <option key={slot} value={slot}>{slot}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>

                            {error && (
                                <p className="text-red-500 text-xs mt-3 text-center">{error}</p>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 flex-shrink-0">
                            <button
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                className="w-full py-3 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
                            >
                                {isLoading ? 'Rescheduling…' : 'Confirm Reschedule'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RescheduleModal;
